/**
 * `fightLayout(viewport, safeArea, textScale, mode)` — the canonical battle rect table (ui-ux §6.12.1, R28; GDD N27,
 * C180, §10.1.6). One pure function produces every rect of the Поединок HUD and the panel the renderer frames; the
 * battle HUD (P4), the camera (F2/P10) and the capture gates (F4, G-23) all consume it. Owner: UI/Visual (F3).
 *
 * Numbers of record (GDD §0.1 N27): reference phone 390×844 → panel incl. frame band 16,187,358×461; desktop
 * 1440×900 → 18,84,1060×662. The 11 viewports of the table are unit-tested for deep equality.
 *
 * All rects are `{x, y, w, h}` in CSS px from the viewport's top-left with the safe areas applied.
 */
import { heightClass, type HeightClass } from './modes';

export interface Rect { x: number; y: number; w: number; h: number }
export interface Safe { t: number; r: number; b: number; l: number }
export type FightMode = 'arena' | 'dungeon' | 'explore';
export type FightLayoutClass = 'xs' | 'phone' | 'phablet' | 'tablet' | 'land' | 'hd' | 'desk';

export interface FightLayout {
  cls: FightLayoutClass;
  heightClass: HeightClass;
  mode: FightMode;
  viewport: { w: number; h: number };
  safe: Safe;
  /** Banner band (names + ribbons + status rail + «Черёд» row); null on `land` (two overlays instead). */
  banner: Rect | null;
  bannerL: Rect | null;
  bannerR: Rect | null;
  /** «Черёд» row (phone/tablet), the left column (land) or the chronicle-column head (hd/desk). */
  turnOrder: Rect;
  skip: Rect;
  pause: Rect;
  /** Panel incl. the canvas frame band. */
  panel: Rect;
  /** Frame band width (art §7.2: 16 · clamp(panelW / 358, 0.9, 1.75)) and the picture interior the camera frames. */
  frameBand: number;
  inner: Rect;
  /** Transparent bleed ring width (canvas only, no interactive DOM). */
  bleed: number;
  headroom: Rect;
  omen: Rect;
  scrollBand: Rect;
  /** Chronicle line (phone/tablet), the right column (hd/desk), or null (hidden: `xs`, `land`, rule 3). */
  chronicle: Rect | null;
  /** «Пыл» bar (vertical on `land`). */
  fervor: Rect;
  fervorVertical: boolean;
  /** The prayer row (null where the tiles are not in one row: `land`, hd/desk). */
  prayers: Rect | null;
  prayerTiles: [Rect, Rect, Rect];
  /** «Авто» (left) and the pet-order chip / «Не сейчас» (right); mirrored with «Левша». */
  auto: Rect;
  pets: Rect;
  /** Dungeon / explore variant: the verb row (tall phones) and the two verb tiles. */
  verbRow: Rect | null;
  verbBless: Rect | null;
  verbSmite: Rect | null;
  /** Turn-order roundel count shown (3, 2 at `xs` in the short dungeon variant) and whether the «Черёд» label shows. */
  turnRoundels: number;
  turnLabel: boolean;
  /** Slack between the bottom bleed and the chronicle line when the panel is capped. */
  slack: number;
  bottomPad: number;
}

const r = (x: number, y: number, w: number, h: number): Rect => ({ x, y, w, h });

/** Width classes (chronicle C / «Пыл» P / prayer row Q) of §6.12.1 by content width. */
function widthClass(vw: number, contentW: number): { C: number | null; P: number; Q: number } {
  if (vw < 360) return { C: null, P: 20, Q: 68 };
  if (contentW >= 398) return { C: 26, P: 22, Q: 92 };
  if (vw >= 390) return { C: 26, P: 22, Q: 84 };
  if (vw >= 375) return { C: 24, P: 20, Q: 80 };
  return { C: 24, P: 20, Q: 76 };
}

function omenRect(panel: Rect, headroomH: number, classMax: number, desktopTop: boolean): Rect {
  const steps = [80, 72, 64, 56, 52].filter((s) => s <= classMax);
  const topOf = (s: number): number => (desktopTop ? 14 : s >= 64 ? 10 : s >= 56 ? 8 : 4);
  let size = steps[steps.length - 1];
  for (const s of steps) { if (topOf(s) + s <= headroomH) { size = s; break; } }
  return r(Math.round(panel.x + (panel.w - size) / 2), panel.y + topOf(size), size, size);
}

function innerOf(panel: Rect): { band: number; inner: Rect } {
  const s = Math.min(1.75, Math.max(0.9, panel.w / 358));
  const band = Math.round(16 * s * 10) / 10;
  return { band, inner: r(panel.x + band, panel.y + band, panel.w - 2 * band, panel.h - 2 * band) };
}

/** Three prayer tiles centred on `cx`, gap shrunk so they keep ≥ 4 px from the chips at the row ends. */
function tileRow(cx: number, y: number, size: number, gap0: number, chipEnd: number | null): [Rect, Rect, Rect] {
  let gap = gap0;
  if (chipEnd !== null) {
    const maxGroup = 2 * (cx - chipEnd - 4);
    if (3 * size + 2 * gap > maxGroup) gap = Math.max(0, Math.floor((maxGroup - 3 * size) / 2));
  }
  const x0 = Math.round(cx - (3 * size + 2 * gap) / 2);
  return [r(x0, y, size, size), r(x0 + size + gap, y, size, size), r(x0 + 2 * (size + gap), y, size, size)];
}

function mirrorX(rect: Rect, x0: number, w: number): Rect { return r(x0 + w - (rect.x - x0) - rect.w, rect.y, rect.w, rect.h); }

export interface FightLayoutOpts { leftHanded?: boolean }

/**
 * The battle layout. `viewport` = CSS size (`{w,h}` or a `Viewport` with `cssW/cssH`); `safeArea` = insets (CSS px);
 * `textScale` = the «Размер текста» multiplier (the HUD stops at 1.3, R5); `mode` = arena or the dungeon / explore
 * variant (combat v2: the «Благословить» / «Покарать» verb row).
 */
export function fightLayout(viewport: { w: number; h: number } | { cssW: number; cssH: number }, safeArea: Partial<Safe> = {},
  textScale = 1, mode: FightMode = 'arena', opts: FightLayoutOpts = {}): FightLayout {
  const W = 'w' in viewport ? viewport.w : viewport.cssW;
  const H = 'h' in viewport ? viewport.h : viewport.cssH;
  const safe: Safe = { t: safeArea.t ?? 0, r: safeArea.r ?? 0, b: safeArea.b ?? 0, l: safeArea.l ?? 0 };
  const hc = heightClass(H);
  const dungeon = mode !== 'arena';
  const ts = Math.min(Math.max(textScale, 1), 1.3);
  const namesGrow = Math.min(6, Math.round(20 * (ts - 1)));

  if (W > H && H <= 520) return landLayout(W, H, safe, hc, mode, opts);
  if (W >= 1024 && H >= 521) return deskLayout(W, H, safe, hc, mode, namesGrow, opts);

  // ── phone portrait (and phablet / tablet: the phone layout in a centred column) ──
  const cls: FightLayoutClass = W < 360 ? 'xs' : W >= 768 ? 'tablet' : W >= 600 ? 'phablet' : 'phone';
  const colW = cls === 'tablet' ? 640 : cls === 'phablet' ? Math.min(600, W - 32) : W;
  const colX = Math.round((W - colW) / 2);
  const xs = cls === 'xs';
  const g = xs ? 12 : 16;
  const b = xs ? 12 : 16;
  const x = colX + g;
  const w = colW - 2 * g;
  const top = Math.max(safe.t, 4);
  const [A, B] = hc === 'h-tall' || hc === 'h-mid' ? [32, 16] : hc === 'h-short' ? [24, 14] : [22, 12];
  const bannerH = A + namesGrow + B + 32 + 44;
  const banner = r(x, top, w, bannerH);
  const turnOrder = r(x, top + bannerH - 44, w, 44);
  const wc = widthClass(W, w);
  let C = wc.C;
  const P = wc.P, Q = wc.Q;
  const pad = xs ? 4 : 8;
  const tileSize = Math.min(76, Math.max(64, Math.floor(0.18 * W)));
  const chip = xs ? 44 : 48;
  const tall = hc === 'h-tall' || hc === 'h-mid';
  const verbRowH = dungeon && tall ? 52 : 0;
  const verbGap = dungeon && tall ? 8 : 0;

  const bottom = H - safe.b - pad;
  const prayersY = bottom - Q;
  const fervorY = prayersY - 2 - P;
  const panelY = top + bannerH + b;
  const cap = Math.floor(1.29 * w);
  const solve = (chron: number | null): { chronY: number | null; panelH: number; slack: number } => {
    const chronY = chron === null ? null : fervorY - 4 - chron;
    const above = chronY === null ? fervorY : chronY;
    const avail = above - verbGap - verbRowH - b - panelY;
    const panelH = Math.min(avail, cap);
    return { chronY, panelH, slack: avail - panelH };
  };
  let s = solve(C);
  if (C !== null && s.panelH < w) { C = null; s = solve(null); }          // rule 3: hide the chronicle, give it to the panel
  const panel = r(x, panelY, w, s.panelH);
  const headroomH = Math.floor(0.18 * panel.h);
  const headroom = r(x, panel.y, w, headroomH);
  const classMax = cls === 'tablet' ? 72 : W >= 375 ? 64 : 56;
  const omen = omenRect(panel, headroomH, classMax, cls === 'tablet');
  const { band, inner } = innerOf(panel);
  const pause = r(x + w - 44, turnOrder.y, 44, 44);
  let skip = r(pause.x - 8 - 44, turnOrder.y, 44, 44);

  const tileTop = prayersY + Math.min(4, Math.max(2, Q - tileSize - 10));
  const gap0 = cls === 'tablet' ? 16 : tileSize >= 76 ? 14 : 12;
  let auto = r(x, Math.floor(tileTop + tileSize / 2 - chip / 2), chip, chip);
  let pets = r(x + w - chip, auto.y, chip, chip);
  let tiles = tileRow(x + w / 2, tileTop, tileSize, gap0, x + chip);
  let verbRow: Rect | null = null, verbBless: Rect | null = null, verbSmite: Rect | null = null;
  let turnRoundels = 3, turnLabel = !xs;
  if (dungeon) {
    if (tall) {
      verbRow = r(x, panel.y + panel.h + b + s.slack, w, 52);
      const vw = Math.floor((w - 8) / 2);
      verbBless = r(x, verbRow.y, vw, 52);
      verbSmite = r(x + w - vw, verbRow.y, vw, 52);
    } else {
      // short phones: the verbs (48) take the row ends; «Авто» and the pet chip move into the «Черёд» row left of skip
      const v = 48;
      verbBless = r(x, Math.floor(tileTop + tileSize / 2 - v / 2), v, v);
      verbSmite = r(x + w - v, verbBless.y, v, v);
      const gapB = 6;
      skip = r(pause.x - gapB - 44, turnOrder.y, 44, 44);
      pets = r(skip.x - gapB - 44, turnOrder.y, 44, 44);
      auto = r(pets.x - gapB - 44, turnOrder.y, 44, 44);
      turnRoundels = xs ? 2 : 3;
      turnLabel = false;
      tiles = tileRow(x + w / 2, tileTop, tileSize, gap0, x + v);
    }
  }
  if (opts.leftHanded) {
    tiles = tiles.map((t) => mirrorX(t, x, w)).reverse() as [Rect, Rect, Rect];
    if (!(dungeon && !tall)) { auto = mirrorX(auto, x, w); pets = mirrorX(pets, x, w); }
    if (verbBless && verbSmite && !tall) { verbBless = mirrorX(verbBless, x, w); verbSmite = mirrorX(verbSmite, x, w); }
  }
  return {
    cls, heightClass: hc, mode, viewport: { w: W, h: H }, safe,
    banner, bannerL: null, bannerR: null, turnOrder, skip, pause,
    panel, frameBand: band, inner, bleed: b, headroom, omen, scrollBand: r(x + 8, panel.y, w - 16, headroomH),
    chronicle: s.chronY === null ? null : r(x, s.chronY, w, C!),
    fervor: r(x, fervorY, w, P), fervorVertical: false,
    prayers: r(x, prayersY, w, Q), prayerTiles: tiles, auto, pets,
    verbRow, verbBless, verbSmite, turnRoundels, turnLabel, slack: s.slack, bottomPad: pad,
  };
}

/** `land` (e.g. 844×390): «Черёд» column left, 16:10 panel, the omen and two banner overlays in the headroom,
 *  a vertical «Пыл» bar and the prayer column right. */
function landLayout(W: number, H: number, safe: Safe, hc: HeightClass, mode: FightMode, opts: FightLayoutOpts): FightLayout {
  const b = 16;
  const colW = 72;
  const left = safe.l;
  const right = W - safe.r;
  const tile = 76;
  const tilesX = right - 8 - tile;
  const fervorX = tilesX - 8 - 12;
  const panelX = left + colW + b;
  const maxW = fervorX - 3 - b - panelX;
  let panelH = H - safe.b - 2 * b;
  let panelW = Math.round(panelH * 1.6);
  if (panelW > maxW) { panelW = maxW; panelH = Math.floor(panelW / 1.6); }
  const panel = r(panelX, b, panelW, panelH);
  const headroomH = Math.floor(0.18 * panelH);
  const headroom = r(panel.x, panel.y, panel.w, headroomH);
  const omen = omenRect(panel, headroomH, 56, false);
  const bannerL = r(panel.x + 16, panel.y + 6, 144, 52);
  const bannerR = r(panel.x + panel.w - 16 - 144, panel.y + 6, 144, 52);
  const turnOrder = r(left, 8, colW, 140);
  const pause = r(left + (colW - 44) / 2, H - safe.b - 8 - 44, 44, 44);
  const skip = r(pause.x, pause.y - 8 - 44, 44, 44);
  let tiles: [Rect, Rect, Rect] = [0, 1, 2].map((i) => r(tilesX, 16 + i * (tile + 8), tile, tile)) as [Rect, Rect, Rect];
  const chipY = 16 + 3 * tile + 2 * 8 + 12;
  let pets = r(right - 44, chipY, 44, 44);
  let auto = r(pets.x - 4 - 44, chipY, 44, 44);
  const fervor = r(fervorX, 16, 12, 3 * tile + 2 * 8);
  const { band, inner } = innerOf(panel);
  let verbBless: Rect | null = null, verbSmite: Rect | null = null;
  if (mode !== 'arena') {
    verbBless = r(auto.x, chipY + 44 + 8, 44, 44);
    verbSmite = r(pets.x, chipY + 44 + 8, 44, 44);
  }
  let turn = turnOrder, sk = skip, pa = pause, fv = fervor;
  if (opts.leftHanded) {            // «Левша» mirrors the landscape columns (the panel stays)
    const m = (x: Rect): Rect => mirrorX(x, 0, W);
    tiles = tiles.map(m) as [Rect, Rect, Rect]; auto = m(auto); pets = m(pets); turn = m(turn); sk = m(sk); pa = m(pa); fv = m(fv);
    if (verbBless && verbSmite) { verbBless = m(verbBless); verbSmite = m(verbSmite); }
  }
  return {
    cls: 'land', heightClass: hc, mode, viewport: { w: W, h: H }, safe,
    banner: null, bannerL, bannerR, turnOrder: turn, skip: sk, pause: pa,
    panel, frameBand: band, inner, bleed: b, headroom, omen,
    scrollBand: r(bannerL.x + bannerL.w, panel.y, bannerR.x - (bannerL.x + bannerL.w), headroomH),
    chronicle: null, fervor: fv, fervorVertical: true, prayers: null, prayerTiles: tiles, auto, pets,
    verbRow: null, verbBless, verbSmite, turnRoundels: 3, turnLabel: false, slack: 0, bottomPad: safe.b,
  };
}

/** hd / desk (≥ 1024 wide): a 16:10 panel + a 328 px chronicle column; rail hidden; the bottom row under «Пыл». */
function deskLayout(W: number, H: number, safe: Safe, hc: HeightClass, mode: FightMode, namesGrow: number, opts: FightLayoutOpts): FightLayout {
  const desk = W >= 1366 && H >= 800;
  const cls: FightLayoutClass = desk ? 'desk' : 'hd';
  const b = 16, colW = 328, colGap = 16, minMargin = 18;
  const top = Math.max(safe.t, 4);
  const bannerH = 64 + namesGrow;
  const panelY = top + bannerH + b;
  const below = b + 4 + 22 + 10 + 76 + 13;                    // bleed, gap, «Пыл», gap, tiles, seal overhang + air
  let panelH = H - safe.b - panelY - below;
  let panelW = Math.round(panelH * 1.6);
  const maxW = Math.min(1600, W - safe.l - safe.r) - 2 * minMargin - colGap - colW;
  if (panelW > maxW) { panelW = maxW; panelH = Math.floor(panelW / 1.6); }
  const x0 = safe.l + Math.round((W - safe.l - safe.r - panelW - colGap - colW) / 2);
  const panel = r(x0, panelY, panelW, panelH);
  const colX = x0 + panelW + colGap;
  const bottomPad = desk ? 14 : 9;
  const banner = r(x0, top, panelW, bannerH);
  const turnOrder = r(colX, top, colW, bannerH);
  const pause = r(colX + colW - 44, top + (bannerH - 44) / 2, 44, 44);
  const skip = r(pause.x - 8 - 44, pause.y, 44, 44);
  const headroomH = Math.floor(0.18 * panelH);
  const headroom = r(panel.x, panel.y, panel.w, headroomH);
  const omen = omenRect(panel, headroomH, desk ? 80 : 72, true);
  const fervor = r(panel.x, panel.y + panelH + b + 4, panelW, 22);
  const tileY = fervor.y + fervor.h + 10;
  const tile = 76;
  let tiles = tileRow(panel.x + panelW / 2, tileY, tile, 16, null);
  let auto = r(panel.x, tileY + tile / 2 - 24, 48, 48);
  let pets = r(panel.x + panelW - 48, auto.y, 48, 48);
  let verbBless: Rect | null = null, verbSmite: Rect | null = null;
  if (mode !== 'arena') {
    const vw = 96, vh = 64;
    const midL = (auto.x + auto.w + tiles[0].x) / 2, midR = (tiles[2].x + tile + pets.x) / 2;
    verbBless = r(Math.round(midL - vw / 2), tileY + (tile - vh) / 2, vw, vh);
    verbSmite = r(Math.round(midR - vw / 2), tileY + (tile - vh) / 2, vw, vh);
  }
  if (opts.leftHanded) {
    tiles = tiles.map((t) => mirrorX(t, panel.x, panelW)).reverse() as [Rect, Rect, Rect];
    auto = mirrorX(auto, panel.x, panelW); pets = mirrorX(pets, panel.x, panelW);
  }
  const { band, inner } = innerOf(panel);
  return {
    cls, heightClass: hc, mode, viewport: { w: W, h: H }, safe,
    banner, bannerL: null, bannerR: null, turnOrder, skip, pause,
    panel, frameBand: band, inner, bleed: b, headroom, omen, scrollBand: r(panel.x + 8, panel.y, panelW - 16, headroomH),
    chronicle: r(colX, panelY, colW, H - safe.b - bottomPad - panelY), fervor, fervorVertical: false,
    prayers: null, prayerTiles: tiles, auto, pets, verbRow: null, verbBless, verbSmite,
    turnRoundels: 3, turnLabel: true, slack: 0, bottomPad,
  };
}

/** `__marg.ui.debugBounds()` provider: every named rect of the layout (null rects omitted). */
export function fightBounds(l: FightLayout): Record<string, Rect> {
  const out: Record<string, Rect> = {};
  const put = (k: string, v: Rect | null): void => { if (v) out[k] = v; };
  put('banner', l.banner); put('bannerL', l.bannerL); put('bannerR', l.bannerR);
  put('turnOrder', l.turnOrder); put('skip', l.skip); put('pause', l.pause);
  put('panel', l.panel); put('inner', l.inner); put('headroom', l.headroom); put('omen', l.omen); put('scrollBand', l.scrollBand);
  put('chronicle', l.chronicle); put('fervor', l.fervor); put('prayers', l.prayers);
  l.prayerTiles.forEach((t, i) => put(`prayer${i + 1}`, t));
  put('auto', l.auto); put('pets', l.pets); put('verbRow', l.verbRow); put('verbBless', l.verbBless); put('verbSmite', l.verbSmite);
  return out;
}

/** The transparent bleed ring as four rects around the panel (canvas only; invariant 2). */
export function bleedRing(l: FightLayout): Rect[] {
  const p = l.panel, b = l.bleed;
  return [r(p.x - b, p.y - b, p.w + 2 * b, b), r(p.x - b, p.y + p.h, p.w + 2 * b, b), r(p.x - b, p.y, b, p.h), r(p.x + p.w, p.y, b, p.h)];
}

export const intersects = (a: Rect, c: Rect): boolean => a.x < c.x + c.w && c.x < a.x + a.w && a.y < c.y + c.h && c.y < a.y + a.h;
